import { parseCSV } from './csv';
import { ACTIVE_MEMBERS } from './managers';
import { normalizeManagerName } from './managerStats';

const KEEPER_POSITIONS = new Set(['QB', 'RB', 'WR', 'TE']);

export function normalizeDraftManager(name) {
  return normalizeManagerName(name);
}

export function keeperTier(round) {
  if (round >= 13) return 'cheap upside';
  if (round >= 9) return 'discount aisle';
  if (round >= 6) return 'middle-round bet';
  return 'premium trap';
}

export function keeperRoundLabel(round) {
  return `Round ${round}`;
}

export function keeperLaneCopy(laneKey, pick) {
  if (!pick) return 'No eligible 2025 skill-position pick in this lane.';

  if (laneKey === 'cheapUpside') {
    return `Costs only a ${keeperRoundLabel(pick.round)} pick. This is the cleanest way to keep the 2026 draft mostly intact.`;
  }

  if (laneKey === 'middleBet') {
    return `Costs a ${keeperRoundLabel(pick.round)} pick. This is where value and real opportunity cost start arguing.`;
  }

  return `Costs a ${keeperRoundLabel(pick.round)} pick. Great name, ugly bill.`;
}

export function buildKeeperCostBoard(rows, managers = ACTIVE_MEMBERS) {
  const byManager = new Map(managers.map((manager) => [manager, []]));

  rows.forEach((row) => {
    const manager = normalizeDraftManager(row.Manager);
    if (!byManager.has(manager)) return;

    const round = parseInt(row.Round, 10);
    const overall = parseInt(row.Pick_Overall, 10);
    const position = row.Position;

    if (!Number.isFinite(round) || !KEEPER_POSITIONS.has(position)) return;

    byManager.get(manager).push({
      manager,
      round,
      overall,
      player: row.Player_Name,
      position,
      nflTeam: row.NFL_Team,
      tier: keeperTier(round),
    });
  });

  return managers.map((manager) => {
    const candidates = (byManager.get(manager) || [])
      .sort((a, b) => b.round - a.round || b.overall - a.overall);
    const cheapUpside = candidates[0] || null;
    const middleBet = pickMiddleBet(candidates);
    const premiumTrap = candidates.slice().sort((a, b) => a.round - b.round || a.overall - b.overall)[0] || null;
    const lanes = [
      {
        key: 'cheapUpside',
        label: 'Cheap upside',
        eyebrow: 'KEEP THE PICKS',
        pick: cheapUpside,
        copy: keeperLaneCopy('cheapUpside', cheapUpside),
      },
      {
        key: 'middleBet',
        label: 'Middle-round bet',
        eyebrow: 'THE ARGUMENT',
        pick: middleBet,
        copy: keeperLaneCopy('middleBet', middleBet),
      },
      {
        key: 'premiumTrap',
        label: 'Premium trap',
        eyebrow: 'STICKER SHOCK',
        pick: premiumTrap,
        copy: keeperLaneCopy('premiumTrap', premiumTrap),
      },
    ];

    return {
      manager,
      eligibleCount: candidates.length,
      candidates: candidates.slice(0, 5),
      cheapest: cheapUpside,
      lanes,
      marketRead: keeperMarketRead(manager, lanes),
    };
  });
}

function pickMiddleBet(candidates) {
  const middle = candidates
    .filter((pick) => pick.round >= 6 && pick.round <= 12)
    .sort((a, b) => b.round - a.round || b.overall - a.overall)[0];

  if (middle) return middle;

  return candidates
    .slice()
    .sort((a, b) => Math.abs(a.round - 9) - Math.abs(b.round - 9) || b.round - a.round)[0] || null;
}

function keeperMarketRead(manager, lanes) {
  const cheap = lanes.find((lane) => lane.key === 'cheapUpside')?.pick;
  const premium = lanes.find((lane) => lane.key === 'premiumTrap')?.pick;
  const middle = lanes.find((lane) => lane.key === 'middleBet')?.pick;

  if (!cheap) return `${manager} has no eligible 2025 skill-position draft receipt on this board.`;

  if (cheap.round >= 15 && premium && premium.round <= 3) {
    return `${manager} has the full keeper dilemma: a late receipt on one end and a premium bill on the other.`;
  }

  if (cheap.round >= 13) {
    return `${manager} has at least one cheap receipt, which is exactly how a keeper argument starts.`;
  }

  if (middle) {
    return `${manager} is mostly shopping in the middle rounds, where every keeper starts sounding defensible.`;
  }

  return `${manager} can keep a name, but the draft cost is going to be visible.`;
}

export async function loadKeeperCostBoard(year = 2025, fetchImpl = fetch) {
  const response = await fetchImpl(`/draft_data/${year}_draft.csv`);
  if (!response.ok) return [];

  const text = await response.text();
  if (!text || /^\s*<!doctype html/i.test(text) || /^\s*<html/i.test(text)) return [];

  return buildKeeperCostBoard(parseCSV(text));
}
